import React, { useState, useEffect } from 'react';

import { useTheme } from 'styled-components';
import Moment from 'moment';
import { useHistory } from 'react-router-dom';

import { FaPlus } from 'react-icons/fa';
import { MdErrorOutline } from 'react-icons/md';
import { FaChartPie } from 'react-icons/fa';
import { FaPuzzlePiece } from 'react-icons/fa';
import { FiMonitor } from 'react-icons/fi';

import api from '~/services/api';

import {
  Scroll,
  Container,
  Header,
  Grid,
  GridLine,
  GridHeadItem,
  GridItem,
  Progress,
  Loading,
} from '~/components';

import {
  Card,
  Cards,
  Board,
  Boards,
  LoadArea,
  Percentage,
  Space,
  Percentages,
  SpaceHorizontal,
  Tag,
  Status,
} from './styles';

import { Align } from '../Produtos/styles';

import PainelType from '~/@types/painel';
import Top10Type from '~/@types/top10';
import { sanitize } from '~/helpers/text';

let lang = require('moment/locale/pt-br');
Moment.locale('pt-br', lang);

const Painel: React.FC = () => {
  const history = useHistory();
  const { colors } = useTheme();

  const [inLoad, setInLoad] = useState(false);

  const [informacoes, setInformacoes] = useState<PainelType | null>(null);
  const [topcriticos, setTopcriticos] = useState<Top10Type[]>([]);
  const [topexcessos, setTopexcessos] = useState<Top10Type[]>([]);

  useEffect(() => {
    const fetchPainel = async () => {
      try {
        setInLoad(true);

        setInformacoes(null);

        const response = await api.get(`analise-de-produtos/painel`);

        const { data } = response;

        setInformacoes(data);

        setInLoad(false);
      } catch (e) {
        setInLoad(false);
      }
    };
    fetchPainel();

    const fetchTopCriticos = async () => {
      try {
        setInLoad(true);

        setInformacoes(null);

        const response = await api.get(`analise-de-produtos/top-criticos`);

        const { data } = response;

        setTopcriticos(data);

        setInLoad(false);
      } catch (e) {
        setInLoad(false);
      }
    };
    fetchTopCriticos();

    const fetchTopExcessos = async () => {
      try {
        setInLoad(true);

        setInformacoes(null);

        const response = await api.get(`analise-de-produtos/top-excessos`);

        const { data } = response;

        setTopexcessos(data);

        setInLoad(false);
      } catch (e) {
        setInLoad(false);
      }
    };
    fetchTopExcessos();
  }, []);

  const goto = (pLink: string, pSearch: string) => {
    history.push({
      pathname: pLink,
      search: `?${pSearch}`,
    });
  };

  return (
    <>
      {inLoad ? <Progress /> : null}

      <Scroll>
        <Container>
          <Header
            title="Painel de análise de produtos"
            subtitle="Dashboard da curva ABC, estoque preditivo e gestão de giro dos produtos"
            icon={
              <>
                <FiMonitor size={32} color={colors.IC1} />
              </>
            }
          />

          {inLoad ? (
            <LoadArea>
              <Loading color={colors.IC1} size={42} />
            </LoadArea>
          ) : null}

          {informacoes && informacoes.status ? (
            <Cards>
              <main>
                {informacoes.status.map(informacao => (
                  <Card
                    id={informacao.id}
                    onClick={() =>
                      goto(
                        '/estoque-preditivo',
                        `status=${informacao.descricao}`,
                      )
                    }
                  >
                    <div>
                      <main>
                        <h2>{informacao.descricao}</h2>
                        <h1>{informacao.total}</h1>
                        <p>{informacao.legenda}</p>
                      </main>
                      {informacao.tag !== '' ? (
                        <span className={informacao.tagid}>
                          {informacao.tag}
                        </span>
                      ) : null}
                    </div>
                  </Card>
                ))}
              </main>
            </Cards>
          ) : null}

          {informacoes && informacoes.classeabc ? (
            <Boards>
              <Board>
                <header>
                  <h1>
                    <i>
                      <FaPuzzlePiece size={26} color={colors.IC1} />
                    </i>
                    <div>
                      <span>Distribuição por classe</span>
                      <p>A/B/C/N/SE para visão de mix.</p>
                    </div>
                  </h1>
                  <button
                    title="Ver lista completa"
                    onClick={() => goto('/curva-abc', ``)}
                  >
                    <FaPlus size={22} color={colors.IC1} />
                  </button>
                </header>
                <main>
                  <Cards>
                    <main>
                      {informacoes.classeabc.map(informacao => (
                        <Card
                          className="card-20"
                          id={informacao.id}
                          onClick={() =>
                            goto('/curva-abc', `classeabc=${informacao.tag}`)
                          }
                        >
                          <div>
                            <main>
                              <h2>{informacao.descricao}</h2>
                              <h1>{informacao.total}</h1>
                              <p>{informacao.legenda}</p>
                            </main>
                            <span className={informacao.tagid}>
                              {informacao.tag}
                            </span>
                          </div>
                        </Card>
                      ))}
                    </main>
                  </Cards>
                </main>
              </Board>
            </Boards>
          ) : null}

          <SpaceHorizontal />

          {informacoes && informacoes.grafico ? (
            <Boards>
              <Board>
                <header>
                  <h1>
                    <i>
                      <FaChartPie size={26} color={colors.IC1} />
                    </i>
                    <div>
                      <span>Curva ABC</span>
                      <p>
                        Distribuição estimada por % acumulado (A ≤ 80, B ≤ 95).
                      </p>
                    </div>
                  </h1>
                  <button
                    title="Ver lista completa"
                    onClick={() => goto('/curva-abc', ``)}
                  >
                    <FaPlus size={22} color={colors.IC1} />
                  </button>
                </header>
                <main>
                  <Percentages>
                    {informacoes.grafico.map(item => (
                      <Percentage id={item.id}>
                        <div>
                          <span>
                            <strong>{item.nome}</strong>
                          </span>
                          <small>{item.valor}</small>
                        </div>
                        <p>
                          <i
                            title={`${item.porcentagem}%`}
                            style={{ width: `${item.porcentagem}%` }}
                          >
                            {`${item.porcentagem}%`}
                          </i>
                        </p>
                      </Percentage>
                    ))}
                  </Percentages>
                </main>
              </Board>
            </Boards>
          ) : null}

          <SpaceHorizontal />

          <Boards>
            {topcriticos.length ? (
              <Board>
                <header>
                  <h1>
                    <i>
                      <MdErrorOutline size={26} color={colors.IC1} />
                    </i>
                    <div>
                      <span>Top Críticos</span>
                      <small>Ação Imediata</small>
                      <p>
                        Ruptura / Urgente / Em falta — ordenado por sugestão.
                      </p>
                    </div>
                  </h1>
                  <button
                    title="Ver lista completa"
                    onClick={() =>
                      goto('/estoque-preditivo', `status=Ruptura Crítica`)
                    }
                  >
                    <FaPlus size={22} color={colors.IC1} />
                  </button>
                </header>
                <main>
                  <Grid>
                    <GridLine>
                      <GridHeadItem title="SKU / Item" />
                      <GridHeadItem title="Classe" />
                      <GridHeadItem title="Giro" width={140} />
                      <GridHeadItem title="Comprar" width={80} />
                    </GridLine>

                    {topcriticos.map(top10 => (
                      <GridLine key={top10.idproduto}>
                        <GridItem>
                          <Align>
                            <strong>{top10.sku}</strong> <br />
                            {top10.nome}
                          </Align>
                        </GridItem>
                        <GridItem width={80}>
                          <Align>
                            <Tag
                              className={`classe-${sanitize(
                                top10.financeiro.classeabc,
                              )}`}
                            >
                              {top10.financeiro.classeabc}
                            </Tag>
                          </Align>
                        </GridItem>
                        <GridItem width={140}>
                          <Align>
                            <Align>
                              <Status className={sanitize(top10.giro)}>
                                {top10.giro}
                              </Status>
                            </Align>
                          </Align>
                        </GridItem>
                        <GridItem width={80}>
                          <Align>{top10.preditivo.sugestaocompra}</Align>
                        </GridItem>
                      </GridLine>
                    ))}
                  </Grid>
                </main>
              </Board>
            ) : null}

            <Space />

            {topexcessos.length ? (
              <Board>
                <header>
                  <h1>
                    <i>
                      <MdErrorOutline size={26} color={colors.IC1} />
                    </i>
                    <div>
                      <span>Top Excesso</span>
                      <small>Estoque Alto</small>
                      <p>
                        Candidatos a ajuste de compra, promo, queima, bundle.
                      </p>
                    </div>
                  </h1>
                  <button
                    title="Ver lista completa"
                    onClick={() =>
                      goto('/estoque-preditivo', `status=Estoque Alto`)
                    }
                  >
                    <FaPlus size={22} color={colors.IC1} />
                  </button>
                </header>
                <main>
                  <Grid>
                    <GridLine>
                      <GridHeadItem title="SKU / Item" />
                      <GridHeadItem title="Classe" width={80} />
                      <GridHeadItem title="Giro" width={140} />
                    </GridLine>

                    {topexcessos.map(top10 => (
                      <GridLine key={top10.idproduto}>
                        <GridItem>
                          <Align>
                            <strong>{top10.sku}</strong> <br />
                            {top10.nome}
                          </Align>
                        </GridItem>
                        <GridItem width={80}>
                          <Tag
                            className={`classe-${sanitize(
                              top10.financeiro.classeabc,
                            )}`}
                          >
                            {top10.financeiro.classeabc}
                          </Tag>
                        </GridItem>
                        <GridItem width={140}>
                          <Align>
                            <Align>
                              <Status className={sanitize(top10.giro)}>
                                {top10.giro}
                              </Status>
                            </Align>
                          </Align>
                        </GridItem>
                      </GridLine>
                    ))}
                  </Grid>
                </main>
              </Board>
            ) : null}
          </Boards>

          <SpaceHorizontal />
        </Container>
      </Scroll>
    </>
  );
};

export default Painel;
